import React, { useRef, useEffect } from 'react';
import PhysicalProfileImage from '../assets/profile/Physical.png';
import SpiritualProfileImage from '../assets/profile/Spiritual.png';
import NutritionalProfileImage from '../assets/profile/Nutritional.png';
import EmotioPsychoRationalProfileImage from '../assets/profile/EPR.png';
import LegendarialProfileImage from '../assets/profile/Legendarial.png';
import ReligioTraditioCulturalProfileImage from '../assets/profile/RTC.png';
import SocioRelationalProfileImage from '../assets/profile/Socio.png';
import FinancialProfileImage from '../assets/profile/Financial.png';
import EnvironmentalProfileImage from '../assets/profile/Environmental.png';
import OccupationalProfileImage from '../assets/profile/Occupational.png';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import ChatDisclaimerModal from '../components/ChatDisclaimerModal';
import { getCachedCoaches, setCachedCoaches, getCachedLayout, setCachedLayout, getCachedMessages, setCachedMessages, markTouched } from '../utils/cache/chatCache';
import { formatDate } from '../utils/dateFormatter';
import {
  Smile,
  Send,
  AlertTriangle,
  UserCircle,
  Video,
  ArrowLeft
} from 'lucide-react';

type ChatMsg = { 
  from: 'me' | 'them'; 
  text: string; 
  ts: string;
  isTyping?: boolean;
  displayText?: string;
};

const ChatPage: React.FC = () => {
  const { token, user, updateTokenUsage, tokenUsage } = useAuth();
  
  // Make cache key user-specific to prevent data leakage between users
  const CHAT_CACHE_KEY = user?.id ? `lifepsensor_chat_cache_${user.id}` : 'lifepsensor_chat_cache';
  // Active coach uses backend coachType ids (e.g., general, physical_health, wellness, spiritual, etc.)
  const [active, setActive] = React.useState<string>('general');
  const [input, setInput] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  
  // Check for cache immediately to determine initial loading state
  const cachedData = localStorage.getItem(CHAT_CACHE_KEY);
  const hasCache = !!cachedData;
  
  const [messages, setMessages] = React.useState<Record<string, ChatMsg[]>>({});
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(!hasCache);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasLoadedFromCache, setHasLoadedFromCache] = React.useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = React.useState(false);
  const [isMobileChatView, setIsMobileChatView] = React.useState(false);
  const [hasReachedDailyLimit, setHasReachedDailyLimit] = React.useState(false);
  
  // Pagination state for each coach
  type PaginationInfo = { hasMore: boolean; nextOffset: number; totalMessages: number };
  const [pagination, setPagination] = React.useState<Record<string, PaginationInfo>>({});

  // Coaches loaded from backend
  const [coaches, setCoaches] = React.useState<Array<{ id: string; name: string; emoji: string; avatarUrl?: string; tags?: string[]; availability?: 'online' | 'away' | 'offline' }>>([]);

  // Map coach IDs to BackgroundSamples images
  const getCoachAvatarUrl = (coachId: string): string => {
    const avatarMap: Record<string, string> = {
      'general': '/BackgroundSamples/Spiritual.png', // Main coach
      'physical_health': PhysicalProfileImage,
      // Backend sometimes uses 'fitness-diet' for Physical Fitness Coach
      'fitness-diet': PhysicalProfileImage,
      'wellness': EmotioPsychoRationalProfileImage,
      'spiritual': SpiritualProfileImage,
      'nutritional': NutritionalProfileImage,
      'emotio_psycho_rational': EmotioPsychoRationalProfileImage,
      'legendarial': LegendarialProfileImage,
      'religio_traditio_cultural': ReligioTraditioCulturalProfileImage,
      'socio_relational': SocioRelationalProfileImage,
      'financial': FinancialProfileImage,
      'environmental': EnvironmentalProfileImage,
      'occupational': OccupationalProfileImage
    };
    return avatarMap[coachId] || SpiritualProfileImage; // Default fallback
  };

  // Map coach IDs to chat bubble CSS classes (matching InsightsPage.tsx colors)
  const getCoachBubbleClass = (coachId: string): string => {
    const classMap: Record<string, string> = {
      'general': 'chat-bubble-star-coach', // Star Coach - Gray (neutral)
      'physical_health': 'chat-bubble-physical', // Physical - Amber
      // Alias id for some backend configs
      'fitness-diet': 'chat-bubble-physical',
      'spiritual': 'chat-bubble-spiritual', // Spiritual - Purple-blue
      'nutritional': 'chat-bubble-nutritional', // Nutritional - Bright green
      'emotio_psycho_rational': 'chat-bubble-emotio-psycho-rational', // Emotio-Psycho-Rational - Magenta
      'legendarial': 'chat-bubble-legendarial', // Legendarial - Orange
      'religio_traditio_cultural': 'chat-bubble-religio-traditio-cultural', // Religio-Traditio-Cultural - Lime green
      'socio_relational': 'chat-bubble-socio-relational', // Socio-Relational - Red
      'financial': 'chat-bubble-financial', // Financial - Cyan
      'environmental': 'chat-bubble-environmental', // Environmental - Purple
      'occupational': 'chat-bubble-occupational' // Occupational - Indigo
    };
    return classMap[coachId] || 'chat-bubble-star-coach'; // Default fallback
  };

  // Layout metadata from backend or cache
  const [layout, setLayout] = React.useState<{ tabs: string[]; header?: string; featuredCoachIds?: string[] }>({
    tabs: ['general', 'physical_health', 'spiritual', 'nutritional', 'emotio_psycho_rational', 'legendarial', 'religio_traditio_cultural', 'socio_relational', 'financial', 'environmental', 'occupational'],
    header: 'Choose your coach',
    featuredCoachIds: ['general']
  });

  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const thread = messages[active] || [];

  // Auto-scroll to bottom when messages change or coach changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, active]);

  // Auto-scroll to bottom when typing animation is happening
  useEffect(() => {
    if (thread.some(msg => msg.isTyping)) {
      scrollToBottom();
    }
  }, [thread]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showEmojiPicker && !(event.target as Element).closest('.chat-input__emoji-container')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Check if user has scrolled up from bottom
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setShowScrollButton(!isAtBottom && thread.length > 0);
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, [thread.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper to get coach meta by id
  const coachMeta = React.useMemo(() => {
    const coach = coaches.find(c => c.id === active);
    if (coach) {
      // Hardcode the correct names for testing
      const hardcodedNames = {
        'general': 'Star Coach',
        'physical_health': 'Physical Fitness Coach',
        'fitness-diet': 'Physical Fitness Coach',
        'spiritual': 'Spiritual Coach',
        'nutritional': 'Nutritional Coach',
        'emotio_psycho_rational': 'Emotio-Psycho-Rational Coach',
        'legendarial': 'Legendarial Coach',
        'religio_traditio_cultural': 'Religio-Traditio-Cultural Coach',
        'socio_relational': 'Socio-Relational Coach',
        'financial': 'Financial Coach',
        'environmental': 'Environmental Coach',
        'occupational': 'Occupational Coach'
      };
      return {
        ...coach,
        name: hardcodedNames[coach.id as keyof typeof hardcodedNames] || coach.name
      };
    }
    return coach;
  }, [coaches, active]);

  // Load chat history for a specific coach with pagination
  const loadChatHistory = async (coachKey: string, offset = 0, append = false) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.CHAT_HISTORY}/${coachKey}?limit=20&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.messages.length > 0) {
          // Convert API messages to frontend format
          const formattedMessages: ChatMsg[] = data.data.messages.map((msg: any) => ({
            from: msg.role === 'user' ? 'me' : 'them',
            text: msg.content,
            ts: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }));

          setMessages(prev => ({
            ...prev,
            [coachKey]: append ? [...formattedMessages, ...prev[coachKey]] : formattedMessages
          }));

          // Update pagination state
          setPagination(prev => ({
            ...prev,
            [coachKey]: {
              hasMore: data.data.hasMore,
              nextOffset: data.data.nextOffset,
              totalMessages: data.data.totalMessages
            }
          }));
        } else if (!append) {
          // No history found, add welcome message (only on initial load)
          // Use hardcoded coach names for the welcome message
          const hardcodedNames = {
            'general': 'Star Coach',
            'physical_health': 'Physical Fitness Coach',
            'fitness-diet': 'Physical Fitness Coach',
            'spiritual': 'Spiritual Coach',
            'nutritional': 'Nutritional Coach',
            'emotio_psycho_rational': 'Emotio-Psycho-Rational Coach',
            'legendarial': 'Legendarial Coach',
            'religio_traditio_cultural': 'Religio-Traditio-Cultural Coach',
            'socio_relational': 'Socio-Relational Coach',
            'financial': 'Financial Coach',
            'environmental': 'Environmental Coach',
            'occupational': 'Occupational Coach'
          };
          const coachName = hardcodedNames[coachKey as keyof typeof hardcodedNames] || coachMeta?.name || 'Coach';
          const coachEmoji = coachMeta?.emoji || '';

          const welcomeMessage: ChatMsg = {
            from: 'them',
            text: `Hello! I'm your ${coachName} ${coachEmoji} How can I help you today?`,
            ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };

          setMessages(prev => ({
            ...prev,
            [coachKey]: [welcomeMessage]
          }));

          setPagination(prev => ({
            ...prev,
            [coachKey]: {
              hasMore: false,
              nextOffset: 0,
              totalMessages: 0
            }
          }));
        }
      }
    } catch (error) {

      if (!append) {
        // Add default welcome message on error (only on initial load)
        // Use hardcoded coach names for the welcome message
        const hardcodedNames = {
          'general': 'Star Coach',
          'physical_health': 'Physical Fitness Coach',
          'fitness-diet': 'Physical Fitness Coach',
          'spiritual': 'Spiritual Coach',
          'nutritional': 'Nutritional Coach',
          'emotio_psycho_rational': 'Emotio-Psycho-Rational Coach',
          'legendarial': 'Legendarial Coach',
          'religio_traditio_cultural': 'Religio-Traditio-Cultural Coach',
          'socio_relational': 'Socio-Relational Coach',
          'financial': 'Financial Coach',
          'environmental': 'Environmental Coach',
          'occupational': 'Occupational Coach'
        };
        const coachName = hardcodedNames[coachKey as keyof typeof hardcodedNames] || coachMeta?.name || 'Coach';
        const coachEmoji = coachMeta?.emoji || '';

        const welcomeMessage: ChatMsg = {
          from: 'them',
          text: `Hello! I'm your ${coachName} ${coachEmoji} How can I help you today?`,
          ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => ({
          ...prev,
          [coachKey]: [welcomeMessage]
        }));

        setPagination(prev => ({
          ...prev,
          [coachKey]: {
            hasMore: false,
            nextOffset: 0,
            totalMessages: 0
          }
        }));
      }
    }
  };

  // Load more messages function
  const loadMoreMessages = async () => {
    const activePage: PaginationInfo = pagination[active] || { hasMore: false, nextOffset: 0, totalMessages: 0 };
    if (isLoadingMore || !activePage.hasMore) return;
    
    setIsLoadingMore(true);
    try {
      await loadChatHistory(active, activePage.nextOffset, true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Load from legacy cache on first mount (for backward compatibility, will be replaced by new cache system)
  React.useEffect(() => {
    const loadLegacyCache = () => {
      // Only load legacy cache if new cache system doesn't have data
      if (!user?.id) return;

      const newCachedMessages = getCachedMessages(user.id);
      if (newCachedMessages) {
        // New cache has data, skip legacy
        return;
      }

      // Try legacy cache
      const cached = localStorage.getItem(CHAT_CACHE_KEY);
      if (cached) {
        try {
          const cachedMessages = JSON.parse(cached);
          setMessages(cachedMessages);
          setHasLoadedFromCache(true);

          // Migrate to new cache system
          setCachedMessages(user.id, {
            messages: cachedMessages,
            pagination: {} // Legacy cache doesn't have pagination
          });

          // Clear legacy cache
          localStorage.removeItem(CHAT_CACHE_KEY);
        } catch (error) {

          localStorage.removeItem(CHAT_CACHE_KEY);
        }
      }
    };

    // Use requestIdleCallback for non-blocking cache loading
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadLegacyCache, { timeout: 1000 });
    } else {
      setTimeout(loadLegacyCache, 100);
    }
  }, [user?.id]);

  // Save to cache whenever messages or pagination change
  React.useEffect(() => {
    if (user?.id && (hasLoadedFromCache || Object.values(messages).some(thread => thread.length > 0))) {
      setCachedMessages(user.id, {
        messages,
        pagination
      });
    }
  }, [messages, pagination, hasLoadedFromCache, user?.id]);

  // Fetch available coaches from cache/backend and initialize state
  React.useEffect(() => {
    const fetchCoaches = async () => {
      const userId = user?.id;
      if (!userId) return;

      // First, try to load from cache for instant UI
      const cachedCoaches = getCachedCoaches(userId);
      if (cachedCoaches && cachedCoaches.length > 0) {
        // Update cached coaches with new avatar URLs
        const updatedCachedCoaches = cachedCoaches.map(coach => ({
          ...coach,
          avatarUrl: getCoachAvatarUrl(coach.id)
        }));
        setCoaches(updatedCachedCoaches);
        // Update cache with new avatar URLs
        setCachedCoaches(userId, updatedCachedCoaches);
        // Initialize message and pagination containers for each coach
        setMessages(prev => {
          const next = { ...prev } as Record<string, ChatMsg[]>;
          cachedCoaches.forEach(c => { if (!next[c.id]) next[c.id] = []; });
          return next;
        });
        setPagination(prev => {
          const next = { ...prev } as Record<string, { hasMore: boolean; nextOffset: number; totalMessages: number }>;
          cachedCoaches.forEach(c => { if (!next[c.id]) next[c.id] = { hasMore: false, nextOffset: 0, totalMessages: 0 }; });
          return next;
        });
        // Set default active
        setActive(cachedCoaches.find(c => c.id === 'general')?.id || cachedCoaches[0]?.id || 'general');
      }

      // Then fetch from backend in background for fresh data
      try {
        const res = await fetch(`${API_CONFIG.ENDPOINTS.CHAT}/coaches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data?.coaches) {
            const list: Array<{ id: string; name: string; emoji: string; avatarUrl?: string; tags?: string[]; availability?: 'online' | 'away' | 'offline' }> = data.data.coaches.map((coach: any) => ({
              ...coach,
              avatarUrl: getCoachAvatarUrl(coach.id), // Override with BackgroundSamples images
              availability: coach.availability || 'online' // Default to online if not specified
            }));

            // Only update state if we got different data
            setCoaches(current => {
              const hasChanged = current.length !== list.length ||
                current.some((c, i) => c.id !== list[i]?.id || c.name !== list[i]?.name);

              if (hasChanged) {
                // Cache the fresh data with updated avatar URLs
                setCachedCoaches(userId, list);
                return list;
              }
              return current;
            });
          }
        }
      } catch (e) {

        // Keep cached data if fetch fails
      }
    };

    if (token && user?.id) fetchCoaches();
  }, [token, user?.id]);

  // Fetch layout metadata from cache/backend
  React.useEffect(() => {
    const fetchLayout = async () => {
      const userId = user?.id;
      if (!userId) return;

      // First, try to load from cache for instant UI
      const cachedLayout = getCachedLayout(userId);
      if (cachedLayout) {
        setLayout(cachedLayout);
      }

      // Then fetch from backend in background for fresh data
      // Note: If backend doesn't have a layout endpoint, we keep the default layout
      try {
        const res = await fetch(`${API_CONFIG.ENDPOINTS.CHAT}/layout`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data?.layout) {
            const layoutData = data.data.layout;
            setCachedLayout(userId, layoutData);
            setLayout(layoutData);
          }
        }
      } catch (e) {

        // Keep cached/default data if fetch fails
      }
    };

    if (token && user?.id) fetchLayout();
  }, [token, user?.id]);

  // Load chat histories after coaches are loaded
  React.useEffect(() => {
    const loadAll = async () => {
      if (coaches.length === 0 || !user?.id) return;

      // Try to load from cache first
      const cachedMessageHistory = getCachedMessages(user.id);
      if (cachedMessageHistory) {
        setMessages(cachedMessageHistory.messages);

        // Ensure pagination has entries for all coaches, merging with cached data
        setPagination(prev => {
          const next = { ...prev } as Record<string, { hasMore: boolean; nextOffset: number; totalMessages: number }>;
          coaches.forEach(c => {
            if (!next[c.id]) {
              next[c.id] = { hasMore: false, nextOffset: 0, totalMessages: 0 };
            }
          });
          // Merge cached pagination data
          Object.keys(cachedMessageHistory.pagination).forEach(coachId => {
            if (next[coachId]) {
              next[coachId] = { ...next[coachId], ...cachedMessageHistory.pagination[coachId] };
            }
          });
          return next;
        });

        setIsLoadingHistory(false);
        return; // Don't fetch from backend, use cache
      }

      // No cache, fetch from backend
      setIsLoadingHistory(true);
      await Promise.all(coaches.map(c => loadChatHistory(c.id)));
      setIsLoadingHistory(false);

      // Cache the loaded messages
      setCachedMessages(user.id, {
        messages,
        pagination
      });
    };
    if (token && user?.id) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id, coaches.map(c => c.id).join(',')]);

  // Typing animation function
  const simulateTyping = async (text: string, coachKey: string) => {
    const words = text.split(' ');
    let displayedText = '';
    
    for (let i = 0; i < words.length; i++) {
      displayedText += (i > 0 ? ' ' : '') + words[i];
      
      setMessages(prev => ({
        ...prev,
        [coachKey]: prev[coachKey].map((msg, idx) => 
          idx === prev[coachKey].length - 1 // Always target the last message (AI message)
            ? { ...msg, displayText: displayedText, isTyping: i < words.length - 1 }
            : msg
        )
      }));
      
      // Variable delay for natural typing rhythm (3x faster)
      const delay = words[i].length > 6 ? 50 : 27; // Longer words = longer pause
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    // Mark as finished typing and set final text
    setMessages(prev => ({
      ...prev,
      [coachKey]: prev[coachKey].map((msg, idx) => 
        idx === prev[coachKey].length - 1 // Always target the last message (AI message)
          ? { ...msg, text: displayedText, displayText: '', isTyping: false }
          : msg
      )
    }));
  };

  // Clean and format text for better display
  const formatCoachMessage = (text: string): string => {
    return text
      // Fix common formatting issues from AI responses
      .replace(/\n\s*\n/g, '\n') // Remove multiple consecutive line breaks
      .replace(/\n(\d+\.)/g, '\n\n$1') // Add proper spacing before numbered lists
      .replace(/(\d+\.)\s*\n/g, '$1 ') // Fix line breaks in middle of numbered items
      .replace(/\n([A-Z][a-z]+:)/g, '\n\n$1') // Add spacing before category headers
      .trim();
  };

  // Add natural conversation breaks to long responses
  const addNaturalBreaks = (text: string): string => {
    // First clean up the text formatting
    let cleanedText = formatCoachMessage(text);

    // Split long paragraphs into smaller, more conversational chunks
    return cleanedText
      .replace(/([.!?])\s+/g, '$1\n\n') // Add breaks after sentences
      .replace(/(\d+\.\s)/g, '$1') // Remove extra spacing before numbered lists
      .replace(/(\*\*[^*]+\*\*)/g, '\n$1\n') // Add breaks around bold text
      .replace(/\n{3,}/g, '\n\n') // Limit to double line breaks
      .trim();
  };

  const send = async () => {
    if (!input.trim() || isLoading) return;

    // Check word count before sending (200 word limit)
    const wordCount = input.trim().split(/\s+/).length;
    if (wordCount > 200) {
      toast.error(`Your message is too long (${wordCount} words). Please keep messages under 200 words.`, {
        position: "top-center",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const userMessage = input.trim();
    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Mark cache as touched (indicates user activity)
    if (user?.id) {
      markTouched(user.id, { lastActiveCoachId: active, lastOpenedAt: new Date().toISOString() });
    }

    // Add user message immediately
    setMessages(prev => ({
      ...prev,
      [active]: [...(prev[active] || []), { from: 'me', text: userMessage, ts: timestamp }],
    }));
    setInput('');
    setIsLoading(true);

    try {
      // Call your Groq API
      const response = await fetch(API_CONFIG.ENDPOINTS.CHAT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          coachType: active
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Track token usage
        if (data.data.usage && data.data.usage.totalTokens) {
          updateTokenUsage(data.data.usage.totalTokens);
        }

        // Trigger subscription data refresh (for message count updates)
        window.dispatchEvent(new CustomEvent('subscriptionRefresh'));
        localStorage.setItem('subscriptionRefresh', Date.now().toString());
        
        // Add AI response with typing animation
        const processedText = addNaturalBreaks(data.data.response);
        const aiMessage: ChatMsg = {
          from: 'them',
          text: '', // Start with empty text
          displayText: '', // This will be updated during typing
          isTyping: true,
          ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        // Add the message first (with empty text)
        setMessages(prev => ({
          ...prev,
          [active]: [...(prev[active] || []), aiMessage]
        }));

        // Update pagination state (new message added)
        setPagination(prev => ({
          ...prev,
          [active]: {
            ...(prev[active] || { hasMore: false, nextOffset: 0, totalMessages: 0 }),
            totalMessages: (prev[active]?.totalMessages || 0) + 2 // User message + AI response
          }
        }));

        // Start typing animation after a small delay (3x faster)
        setTimeout(() => {
          simulateTyping(processedText, active);
        }, 167); // Small delay before starting to type (3x faster)

      } else {
        // Check if this is a daily limit error (HTTP 429 or specific message)
        const isDailyLimitError = response.status === 429 ||
          (data.message && data.message.includes('Maximum chats reached for today'));

        // Check if this is a word limit error (HTTP 400 with word count message)
        const isWordLimitError = response.status === 400 &&
          (data.message && data.message.includes('too long') && data.message.includes('words'));

        if (isDailyLimitError) {
          // Show toast notification for daily limit and don't add bot message to chat
          toast.error('You\'ve reached your maximum 10 chats for today. Please come back tomorrow.', {
            position: "top-center",
            autoClose: 8000, // Longer duration for important limits
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          // Set flag to disable send functionality
          setHasReachedDailyLimit(true);
        } else if (isWordLimitError) {
          // Show toast notification for word limit and don't add bot message to chat
          toast.error(data.message, {
            position: "top-center",
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          // Add error message with specific backend message for other errors
          setMessages(prev => ({
            ...prev,
            [active]: [...(prev[active] || []), {
              from: 'them',
              text: data.message || 'Sorry, I\'m having trouble responding right now. Please try again.',
              ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }],
          }));
        }
      }
    } catch (error) {

      // Determine error message based on error type
      let errorMessage = 'Sorry, I\'m having trouble responding right now. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Connection error. Please check your internet connection and try again.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Session expired. Please refresh the page and log in again.';
        } else if (error.message.includes('429')) {
          errorMessage = 'Chat service is busy. Please try again in a moment.';
        }
      }
      
      // Add error message
      setMessages(prev => ({
        ...prev,
        [active]: [...(prev[active] || []), { 
          from: 'them', 
          text: errorMessage, 
          ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
        }],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const lastOf = (key: string): ChatMsg | undefined => {
    const arr = messages[key] || [];
    return arr[arr.length - 1];
  };

  // Emoji picker functionality
  const commonEmojis = [
    '😀', '😂', '😊', '😍', '🥰', '😘', '😉', '😎', '🤔', '😮', '😢', '😭', '😤', '😡',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '🙏', '💪', '❤️', '💯', '🔥', '⭐',
    '🎉', '🎊', '🎈', '🎁', '🌟', '✨', '💫', '🌈', '☀️', '🌙', '⏰', '📱', '💻', '⌚'
  ];

  const handleEmojiSelect = (emoji: string) => {
    setInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Show loading screen only if no cache and still loading
  if (isLoadingHistory && !hasLoadedFromCache) {
    return <LoadingScreen message="Loading chat history..." fullScreen />;
  }

  return (
    <div className={`chat chat--nature ${window.innerWidth <= 768 ? (isMobileChatView ? 'chat--mobile-chat-view' : 'chat--mobile-list-view') : ''}`}>
      <div className="chat__main">
        {/* Chat Header */}
        <header className="chat-header">
          <div className="chat-header__main">
            <div className="chat-header__avatar-container">
              <div className={`chat-header__avatar ${active === 'general' ? 'chat-header__avatar--online' : ''}`}>
                {coachMeta?.avatarUrl ? (
                  <img src={coachMeta.avatarUrl} alt={coachMeta.name} className="chat-header__avatar-img" />
                ) : (
                  coachMeta?.emoji || '??'
                )}
              </div>
            </div>
            <div className="chat-header__info">
              <h1 className="chat-header__name">{coachMeta?.name || 'Coach'}</h1>
              <p className="chat-header__subtitle">Coach</p>
            </div>
          </div>
          <div className="chat-header__actions">
            {isMobileChatView && (
              <button
                className="chat-header__back-btn"
                onClick={() => setIsMobileChatView(false)}
                title="Back to coaches"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="chat-header__ai-trigger">
              <div className="chat-header__ai-box">
                AI
              </div>
              {/* Hover Modal */}
              <div className="chat-header__ai-modal">
                <div className="chat-header__modal-content">
                  <h4>AI Usage Summary</h4>
                  <div className="chat-header__usage-stats">
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Total Tokens:</span>
                      <span className="chat-header__stat-value">{(tokenUsage.totalTokens || 0).toLocaleString()}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Total Messages:</span>
                      <span className="chat-header__stat-value">{tokenUsage.messagesCount || 0}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Today's Tokens:</span>
                      <span className="chat-header__stat-value">{(tokenUsage.dailyTokens || 0).toLocaleString()}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Today's Messages:</span>
                      <span className="chat-header__stat-value">{tokenUsage.dailyMessages || 0}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">This Year's Tokens:</span>
                      <span className="chat-header__stat-value">{(tokenUsage.yearlyTokens || 0).toLocaleString()}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">This Year's Messages:</span>
                      <span className="chat-header__stat-value">{tokenUsage.yearlyMessages || 0}</span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Avg per Message:</span>
                      <span className="chat-header__stat-value">
                        {(tokenUsage.messagesCount || 0) > 0
                          ? Math.round((tokenUsage.totalTokens || 0) / (tokenUsage.messagesCount || 1))
                          : 0
                        }
                      </span>
                    </div>
                    <div className="chat-header__stat-item">
                      <span className="chat-header__stat-label">Last Updated:</span>
                      <span className="chat-header__stat-value">
                        {tokenUsage.lastUpdated
                          ? formatDate(tokenUsage.lastUpdated)
                          : 'Never'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Chat Messages */}
        <div className="chat-messages" ref={messagesContainerRef}>
          {isLoadingHistory ? (
            <div className="load-more-container">
              <div className="loading-spinner"></div>
              <span>Loading your chat history...</span>
            </div>
          ) : (
            <>
              {/* Load More button */}
              {(pagination[active]?.hasMore || false) && (
                <div className="load-more-container">
                  <button
                    className="load-more-btn"
                    onClick={loadMoreMessages}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="loading-spinner"></div>
                        Loading more messages...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Load more messages
                      </>
                    )}
                  </button>
                </div>
              )}

              {thread.length === 0 ? (
                <div className="chat-message chat-message--incoming">
                  <div className="chat-message__content">
                    <div
                      className={`chat-message__bubble ${getCoachBubbleClass(active)}`}
                    >
                      <div className="chat-message__text">Start the conversation</div>
                      <div className="chat-message__text">Say hello or ask a question to begin.</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {thread.map((m, idx) => (
            <div key={idx} className={`chat-message ${m.from === 'me' ? 'chat-message--outgoing' : 'chat-message--incoming'}`}>
              <div className={`chat-message__avatar ${m.from === 'me' ? 'chat-message__avatar--user' : 'chat-message__avatar'}`}>
                {m.from === 'them' ? (
                  coachMeta?.avatarUrl ? (
                    <img src={coachMeta.avatarUrl} alt={coachMeta.name} className="chat-message__avatar-img" />
                  ) : (
                    coachMeta?.emoji || '??'
                  )
                ) : (
                  <UserCircle className="w-10 h-10 text-blue-500" />
                )}
              </div>
              <div className="chat-message__content">
                {m.from === 'them' && (
                  <div className="chat-message__header">
                    <span className="chat-message__sender-name">{coachMeta?.name || 'Coach'}</span>
                  </div>
                )}
                <div
                  className={`chat-message__bubble ${m.from === 'them' ? getCoachBubbleClass(active) : ''}`}
                >
                  <div className="chat-message__text">
                    {m.displayText || m.text}
                    {m.isTyping && <span className="typing-cursor">|</span>}
                  </div>
                  <div className={`chat-message__timestamp ${m.from === 'me' ? 'chat-message__timestamp--outgoing' : ''}`}>
                    {m.ts}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="chat-message chat-message--incoming chat-message--typing">
              <div className="chat-message__avatar">
                {coachMeta?.avatarUrl ? (
                  <img src={coachMeta.avatarUrl} alt={coachMeta.name} className="chat-message__avatar-img" />
                ) : (
                  coachMeta?.emoji || '??'
                )}
              </div>
              <div className="chat-message__content">
                <div className="chat-message__header">
                  <span className="chat-message__sender-name">{coachMeta?.name || 'Coach'}</span>
                </div>
                <div
                  className={`chat-message__bubble ${getCoachBubbleClass(active)}`}
                >
                  <div className="typing-indicator">
                    <span className="typing-indicator__dot"></span>
                    <span className="typing-indicator__dot"></span>
                    <span className="typing-indicator__dot"></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scroll anchor for auto-scrolling */}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        <footer className="chat-input">
          <div className="chat-input__container">
            <div className="chat-input__field-wrapper">
              <input
                className="chat-input__field"
                placeholder={hasReachedDailyLimit ? "Daily chat limit reached..." : "Write your message..."}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !hasReachedDailyLimit) send(); }}
                disabled={isLoading || isLoadingHistory || hasReachedDailyLimit}
              />
              <div className="chat-input__emoji-container">
                <button
                  className="chat-input__action-btn"
                  title="Add emoji"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  <Smile className="w-5 h-5" />
                </button>
                {showEmojiPicker && (
                  <div className="chat-input__emoji-picker">
                    <div className="chat-input__emoji-grid">
                      {commonEmojis.map((emoji, index) => (
                        <button
                          key={index}
                          className="chat-input__emoji-btn"
                          onClick={() => handleEmojiSelect(emoji)}
                          title={emoji}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="chat-input__actions">
              <button
                className="chat-input__send-btn"
                onClick={send}
                disabled={isLoading || isLoadingHistory || !input.trim() || hasReachedDailyLimit}
              >
                {hasReachedDailyLimit ? 'Limit Reached' : isLoading ? 'Sending...' : 'Send'}
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* Chat Sidebar */}
      <aside className="chat-sidebar">
        <div className="chat-sidebar__header">
          <h2 className="chat-sidebar__title">Coaches</h2>
        </div>
        <div className="chat-sidebar__coaches">
          {coaches.map(c => {
            const isActive = active === c.id;
            const lastMessage = lastOf(c.id);
            const avatarClass = `chat-sidebar__coach-avatar chat-sidebar__coach-avatar--${c.id}`;

            // Use hardcoded coach names for the sidebar display
            const hardcodedNames = {
              'general': 'Star Coach',
              'physical_health': 'Physical Fitness Coach',
              'fitness-diet': 'Physical Fitness Coach',
              'spiritual': 'Spiritual Coach',
              'nutritional': 'Nutritional Coach',
              'emotio_psycho_rational': 'Emotio-Psycho-Rational Coach',
              'legendarial': 'Legendarial Coach',
              'religio_traditio_cultural': 'Religio-Traditio-Cultural Coach',
              'socio_relational': 'Socio-Relational Coach',
              'financial': 'Financial Coach',
              'environmental': 'Environmental Coach',
              'occupational': 'Occupational Coach'
            };
            const displayName = hardcodedNames[c.id as keyof typeof hardcodedNames] || c.name;

            return (
              <button
                key={c.id}
                className={`chat-sidebar__coach ${isActive ? 'chat-sidebar__coach--active' : ''}`}
                onClick={() => {
                  setActive(c.id);
                  // On mobile, switch to chat view when selecting a coach
                  if (window.innerWidth <= 768) {
                    setIsMobileChatView(true);
                  }
                  // Mark cache as touched when switching coaches
                  if (user?.id) {
                    markTouched(user.id, { lastActiveCoachId: c.id, lastOpenedAt: new Date().toISOString() });
                  }
                }}
              >
                <div className={avatarClass}>
                  {c.avatarUrl ? (
                    <img src={c.avatarUrl} alt={displayName} className="chat-sidebar__coach-avatar-img" />
                  ) : (
                    c.emoji || '??'
                  )}
                </div>
                <div className="chat-sidebar__coach-info">
                  <div className="chat-sidebar__coach-name">{displayName}</div>
                  <div className="chat-sidebar__coach-role">
                    {c.id === 'general' ? 'Coach' : 'Coach'}
                  </div>
                  <div className="chat-sidebar__coach-preview" title={lastMessage?.text || ''}>
                    {lastMessage?.text || 'No messages yet'}
                  </div>
                </div>
                <div className="chat-sidebar__coach-timestamp">
                  {lastMessage?.ts || ''}
                </div>
              </button>
            );
          })}
        </div>

        <div className="chat-sidebar__footer">
          <button
            className="chat-sidebar__notice"
            onClick={() => setShowDisclaimerModal(true)}
          >
            <AlertTriangle className="w-5 h-5" />
            <span>Important Wellness Notice</span>
          </button>
          <button
            className="chat-sidebar__consultation"
            onClick={() => {
              window.open('https://magiteque.life/contact-us/', '_blank');
            }}
          >
            <Video className="w-4 h-4" />
            <span>Consultation</span>
          </button>
        </div>
      </aside>

      {/* Disclaimer Modal */}
      <ChatDisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
      />
    </div>
  );
};

export default ChatPage;


